import { Injectable } from "@angular/core";
import { Prompt } from "@models/prompt.model";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { SmartFeatureSdk } from "@sdks/smart-feature.sdk";
import { EMPTY, Observable, Subject, forkJoin, of, switchMap, takeUntil, tap, timer } from "rxjs";
import { Store } from "@ngrx/store";
import { PromptSdk } from "@sdks/prompt.sdk";
import { promptsActions } from "../ai-insights/prompts/prompts.actions";
import { Clipboard } from '@angular/cdk/clipboard';
import { NotificationManager } from '@managers/notification.manager';
import { ThirdPartyId } from "@models/third-party-id.enum";
import { ClaudeModel } from "@models/amazon-bedrock-claude.model";
import { AppConfigService } from "@services/app-config.service";
import { PilotClient, PilotRequest, PilotRequestStreamingRequest } from "@clients/api.client";

@Injectable({
    providedIn: 'root'
})
export class AiPilotAssistantStore extends ComponentStore<AiPilotAssistantState> {

    constructor(
        private store: Store,
        private smartFeatureSdk: SmartFeatureSdk,
        private pilotClient: PilotClient,
        private promptSdk: PromptSdk,
        private notificationManager: NotificationManager,
        private clipboard: Clipboard,
        private notificationService: NotificationManager,
        private appConfigService: AppConfigService
    ) {
        super({
            workflowKey: null,
            searchType: 0,
            aiModel: null,
            prompts: [],
            currentPrompt: null,
            currentResponse: '',
            status: 'waiting'
        });
    }

    public prompts$: Observable<Prompt[]> = this.select(state => state.prompts);

    public currentPrompt$: Observable<Prompt> = this.select(state => state.currentPrompt);

    public currentResponse$: Observable<string> = this.select(state => state.currentResponse);

    public currentStatus$: Observable<AiPilotAssistantStatus> = this.select(state => state.status);

    public currentWorkflowKey$: Observable<string> = this.select(state => state.workflowKey);

    public readonly addPrompts = this.updater((state, prompts: Prompt[]) => ({ ...state, prompts: prompts }));

    public readonly clearPrompts = this.updater((state) => ({ ...state, prompts: [] }));

    public readonly setPrompt = this.updater((state, prompt: Prompt) => {
        state.currentPrompt = prompt;
        return state;
    });

    public readonly setAiEngineModel = this.updater((state, aiEngineModel: number) => {
        state.aiModel = AiEngineModels.find(x => x.value === aiEngineModel)?.model;
        return state;
    });

    public readonly setSearchType = this.updater((state, searchType: number) => {
        state.searchType = searchType;
        return state;
    });

    public readonly refreshCurrentPrompt = this.updater((state) => ({ ...state, currentPrompt: state.prompts.find(x => x.promptId === state.currentPrompt.promptId) }));

    public readonly cancelRequest = this.updater((state) => {
        this.cancel$.next();
        this.cancel$.complete();
        state.workflowKey = null;
        state.status = 'waiting';
        return state;
    });

    public readonly updateResponse = this.updater((state, response: string) => ({ ...state, currentResponse: response }));

    public readonly initialize = this.updater((state, engineModel: number) => {
        state.currentPrompt = null;
        state.currentResponse = '';
        state.status = 'waiting';
        state.aiModel = AiEngineModels.find(x => x.value === engineModel)?.model;
        return state;
    });

    public readonly answerPrompt = this.effect((trigger$) => {
        return trigger$.pipe(
            tap(() => {
                this.patchState({ status: 'responding', currentResponse: '' });
                this.cancel$ = new Subject<void>();
            }),
            switchMap(() => {
                return this.apiRequest();
            })
        );
    });

    public readonly createAndPrompt = this.effect<string>((prompt$) => {
        return prompt$.pipe(
            tap(() => {
                this.patchState({ status: 'prompting', currentResponse: '' });
            }),
            switchMap((prompt) => {
                return forkJoin([
                    this.promptSdk.post(`Recent Prompt ${new Date().toLocaleString()}`, prompt),
                    this.smartFeatureSdk.generatePromptTitle(prompt, this.externalAppId)
                ]).pipe(
                    tapResponse(
                        (data) => {
                            if (data[1] && data[1].length > 0 && data[1].length < 50) {
                                this.updatePromptTitle({ promptId: data[0].promptId, title: data[1] });
                            }
                            this.patchState({ currentPrompt: data[0] });
                            this.store.dispatch(promptsActions.loadPrompts({ initLoad: false }));
                        },
                        () => this.displayErrorNotification('creating the prompt'),
                        () => this.answerPrompt()
                    )
                );
            })
        )
    });

    public readonly deletePrompt = this.effect<string>((promptId$) => {
        return promptId$.pipe(
            tap((id) => {
                const current = this.get().currentPrompt;
                if (current && current.promptId === id)
                    this.setPrompt(null);
            }),
            switchMap((id) => {
                return this.promptSdk.delete(id).pipe(
                    tapResponse(
                        (isDeleted) => { if (isDeleted) this.store.dispatch(promptsActions.loadPrompts({ initLoad: false })); },
                        () => this.displayErrorNotification('deleting the prompt')
                    )
                )
            })
        )
    });

    public copyResponseToClipboard(): void {
        const responses = this.get().currentResponse;
        const regex = /" target="DocViewer">\d+<\/a>]/g
        this.clipboard.copy(responses
            .replaceAll(regex, '] ')
            .replaceAll('<a href="/review/doc-viewer?', `${this.appConfigService.getConfig().webApi}/review/doc-viewer?`));
        this.notificationService.displaySnackBarMessage({
            message: 'Pilot response copied to clipboard!'
        });
    }

    public readonly scheduleStreaming = this.effect<{ waitInMs: number; autoScroll: () => void }>(
        (trigger$) => trigger$.pipe(
            switchMap(({ waitInMs, autoScroll }) =>
                timer(waitInMs).pipe(
                    switchMap(() => {
                        const workflowKey = this.get().workflowKey;
                        if (!workflowKey) return of(null);
                        const body = new PilotRequestStreamingRequest;
                        body.workflowKey = workflowKey;
                        return this.pilotClient.getPilotResponse$(body);
                    }),
                    tapResponse({
                        next: (response) => {
                            if (!response)
                                this.scheduleStreaming({ waitInMs: 3000, autoScroll });
                            else {
                                let result = '';
                                if (response.result)
                                    result = response?.result;
                                if (response.docIds && response.docIds.length > 0) {
                                    for (let index = 1; index <= response.docIds.length; index++) {
                                        result += ` [<a href="/review/doc-viewer?DocId=${response.docIds[index - 1]}" target="DocViewer">${index}</a>]`;
                                    }
                                }
                                this.updateResponse(result);
                                if (response.result.length > 0)
                                    autoScroll();
                                if (response.completed)
                                    this.patchState({ workflowKey: null, status: 'waiting' });
                                if (!response.completed)
                                    this.scheduleStreaming({ waitInMs: 1000, autoScroll });
                            }
                        },
                        error: () => {
                            this.patchState({ workflowKey: null, status: 'error' });
                            this.updateResponse(this.errorResponse);
                        }
                    })
                )
            )
        )
    );

    private readonly handleResponse = this.effect<string>((workflowKey$) => {
        return workflowKey$.pipe(
            switchMap((workflowKey: string) => {
                this.patchState({ workflowKey: workflowKey });
                return EMPTY;
            })
        );
    });

    private apiRequest(): Observable<any> {
        const body = new PilotRequest;
        body.promptId = this.get().currentPrompt.promptId;
        body.aiModel = this.get().aiModel;
        body.searchType = this.get().searchType;
        const aiRequest: Observable<string> = this.pilotClient.generatePilotResponse$(body).pipe(
            tapResponse((workflowKey) => this.handleResponse(workflowKey),
                () => {
                    this.patchState({ status: 'error' });
                    this.updateResponse(this.errorResponse);
                }));
        return aiRequest.pipe(takeUntil(this.cancel$));
    }

    private readonly updatePromptTitle = this.effect<PromptPatchParameter>((parameter$) => {
        return parameter$.pipe(
            switchMap((parameter) => this.promptSdk.patch(parameter.promptId, { title: parameter.title }).pipe(
                tapResponse(
                    (data) => this.patchState({ currentPrompt: data }),
                    () => this.displayErrorNotification('updating the prompt'),
                    () => this.store.dispatch(promptsActions.loadPrompts({ initLoad: false }))
                ))
            )
        )
    });


    private displayErrorNotification(message: string): void {
        this.notificationManager.displaySnackBarMessage({
            message: `An error has occurred when ${message}. Please try again or contact Professional Service for support.`,
            type: 'error',
            ttl: 5000
        });
    }

    private errorResponse: string = 'An error has occurred. Please try again after a few minutes, or contact Professional Services for more support.';
    private cancel$: Subject<void>;
    private externalAppId = ThirdPartyId.AmazonBedrockClaude;
}

export type AiPilotAssistantStatus = 'waiting' | 'responding' | 'prompting' | 'error'

export const AiEngineModels = [
    { value: 1, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_Sonnet, name: 'Claude 3 Sonnet' },
    { value: 2, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_Sonnet, name: 'Claude 3.5 Sonnet' },
    { value: 3, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_SonnetV2, name: 'Claude 3.5 Sonnet v2' },
    { value: 4, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_7_Sonnet, name: 'Claude 3.7 Sonnet' }
];

export interface AiPilotAssistantState {
    workflowKey?: string;
    aiModel: string;
    searchType: number;
    prompts: Prompt[],
    currentPrompt: Prompt;
    currentResponse: string;
    status: AiPilotAssistantStatus;
}

export interface PromptPatchParameter {
    promptId: string;
    title: string;
}
